import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import html2pdf from 'html2pdf.js';
import { invitationTemplates } from '../utils/templates';
import useThemeStyles from '../theme/useThemeStyles';

function InvitationGenerator() {
  const styles = useThemeStyles();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    groomName: '',
    brideName: '',
    date: '',
    time: '',
    venue: '',
    address: '',
    rsvpDate: '',
    rsvpEmail: '',
    additionalInfo: '',
    template: 'elegant',
  });

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const generateInvitation = () => {
    const template = invitationTemplates[formData.template];
    
    return `
      <div style="
        font-family: ${template.headerFont};
        color: ${template.color};
        background-color: ${template.backgroundColor};
        padding: ${template.padding};
        max-width: 600px;
        margin: 0 auto;
        text-align: center;
        border: ${template.borderWidth} ${template.borderStyle} ${template.borderColor};
      ">
        <h1 style="font-size: 3rem; margin-bottom: 1rem;">
          ${formData.groomName} & ${formData.brideName}
        </h1>
        
        <p style="font-size: 1.2rem; font-style: italic; margin-bottom: 2rem;">
          request the pleasure of your company at their wedding
        </p>
        
        <div style="margin: 2rem 0;">
          <p style="font-size: 1.1rem; margin: 0.5rem 0;">
            ${formData.date}
          </p>
          <p style="font-size: 1.1rem; margin: 0.5rem 0;">
            at ${formData.time}
          </p>
        </div>
        
        <div style="margin: 2rem 0;">
          <p style="font-size: 1.1rem; font-weight: bold; margin: 0.5rem 0;">
            ${formData.venue}
          </p>
          <p style="font-size: 1rem; margin: 0.5rem 0;">
            ${formData.address}
          </p>
        </div>
        
        ${formData.additionalInfo ? `
          <p style="font-size: 0.9rem; margin: 2rem 0; font-style: italic;">
            ${formData.additionalInfo}
          </p>
        ` : ''}
        
        <div style="margin-top: 3rem; font-size: 0.9rem;">
          <p>Please RSVP by ${formData.rsvpDate}</p>
          <p>${formData.rsvpEmail}</p>
        </div>
      </div>
    `;
  };

  const handlePreview = () => {
    const content = generateInvitation();
    const previewWindow = window.open('', '_blank');
    previewWindow.document.write(`
      <html>
        <head>
          <title>Invitation Preview</title>
          <style>
            body { margin: 0; padding: 20px; }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `);
    previewWindow.document.close();
  };

  const handleDownload = async () => {
    setLoading(true);
    const content = generateInvitation();
    const element = document.createElement('div');
    element.innerHTML = content;
    
    const options = {
      margin: 1,
      filename: 'wedding-invitation.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    try {
      await html2pdf().set(options).from(element).save();
    } catch (error) {} finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={styles.container.page}>
      <Typography component="h2" className="pagename" sx={styles.text.pagename}>
        Invitation Generator
      </Typography>
      <Typography variant="body1" sx={styles.text.sectionDescription}>
        Create beautiful wedding invitations with our customizable templates
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={styles.card.default}>
            <CardContent sx={styles.spacing.card}>
              <Typography variant="h6" sx={styles.text.sectionTitle}>
                Invitation Details
              </Typography>
              
              <Box sx={styles.patterns.formContainer}>
                <FormControl fullWidth>
                  <InputLabel>Template</InputLabel>
                  <Select
                    value={formData.template}
                    onChange={handleChange('template')}
                    label="Template"
                  >
                    <MenuItem value="elegant">Elegant</MenuItem>
                    <MenuItem value="modern">Modern</MenuItem>
                    <MenuItem value="rustic">Rustic</MenuItem>
                    <MenuItem value="floral">Floral</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Partner One's Name"
                  value={formData.groomName}
                  onChange={handleChange('groomName')}
                />

                <TextField
                  fullWidth
                  label="Partner Two's Name"
                  value={formData.brideName}
                  onChange={handleChange('brideName')}
                />

                <TextField
                  fullWidth
                  label="Wedding Date"
                  value={formData.date}
                  onChange={handleChange('date')}
                  placeholder="Saturday, June 15th, 2024"
                />

                <TextField
                  fullWidth
                  label="Time"
                  value={formData.time}
                  onChange={handleChange('time')}
                  placeholder="4:00 PM"
                />

                <TextField
                  fullWidth
                  label="Venue Name"
                  value={formData.venue}
                  onChange={handleChange('venue')}
                />

                <TextField
                  fullWidth
                  label="Venue Address"
                  value={formData.address}
                  onChange={handleChange('address')}
                  multiline
                  rows={2}
                />

                <TextField
                  fullWidth
                  label="RSVP Date"
                  value={formData.rsvpDate}
                  onChange={handleChange('rsvpDate')}
                  placeholder="May 1st, 2024"
                />

                <TextField
                  fullWidth
                  label="RSVP Email"
                  value={formData.rsvpEmail}
                  onChange={handleChange('rsvpEmail')}
                  type="email"
                />

                <TextField
                  fullWidth
                  label="Additional Information"
                  value={formData.additionalInfo}
                  onChange={handleChange('additionalInfo')}
                  multiline
                  rows={3}
                  placeholder="Dress code, parking information, etc."
                />
              </Box>

              <Box sx={{ ...styles.layout.flexRow, gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreview}
                  fullWidth
                >
                  Preview
                </Button>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleDownload}
                  disabled={loading}
                  fullWidth
                >
                  Download PDF
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={styles.card.default}>
            <CardContent sx={styles.spacing.card}>
              <Typography variant="h6" sx={styles.text.sectionTitle}>
                Preview
              </Typography>
              
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  minHeight: 400,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  textAlign: 'center',
                }}
              >
                <Typography variant="h4" gutterBottom>
                  {formData.groomName || 'Groom'} & {formData.brideName || 'Bride'}
                </Typography>
                
                <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 3 }}>
                  request the pleasure of your company at their wedding
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {formData.date || 'Wedding Date'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  at {formData.time || 'Time'}
                </Typography>
                
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {formData.venue || 'Venue Name'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  {formData.address || 'Venue Address'}
                </Typography>
                
                {formData.additionalInfo && (
                  <Typography variant="body2" sx={{ mb: 3, fontStyle: 'italic' }}>
                    {formData.additionalInfo}
                  </Typography>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="caption">
                  Please RSVP by {formData.rsvpDate || 'RSVP Date'}
                </Typography>
                <Typography variant="caption" display="block">
                  {formData.rsvpEmail || 'rsvp@example.com'}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default InvitationGenerator; 